import { Suspense, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Award,
  Calculator,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  CreditCard,
  Database,
  FlaskConical,
  Gamepad2,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Send,
  Settings,
  Star,
  UserCheck,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Spinner } from "@/components/Spinner";
import { CloseButton } from "@/components/ui/CloseButton";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { DateRangeProvider } from "@/context/DateRangeContext";
import { prefetchDashboardRoutes } from "@/lib/prefetchDashboardRoutes";
import { NAV_SECTIONS, type NavItem, type NavRole } from "@/pages/PAGES_PLAN";
import { useAuth } from "@/context/AuthContext";
import { useAccountingAccess } from "@/hooks/useAccountingAccess";
import { dataAccessOf, type DataAccess } from "@/lib/dataAccess";
import { ToastContainer } from "@/lib/toast";
import { TopbarContext } from "@/context/TopbarContext";

const ICON_MAP: Record<string, LucideIcon> = {
  LayoutDashboard,
  Users,
  UserCheck,
  Award,
  Gamepad2,
  Megaphone,
  Calculator,
  CreditCard,
  FlaskConical,
  Database,
  Send,
  UserCog,
  Settings,
  Star,
};

function navVisible(
  role: "admin" | "manager" | "marketing" | "user",
  itemRole: NavRole,
  access: DataAccess,
  teamLead = false
): boolean {
  if (itemRole === "all") return true;
  if (itemRole === "leadOnly") return teamLead && role !== "admin";
  // หน้าข้อมูล — ซ่อนจาก user / marketing ไม่มีทีม (กติกาเดียวกับ backend · lib/dataAccess.ts)
  if (itemRole === "data") return access !== "none";
  if (itemRole === "admin") return role === "admin";
  if (itemRole === "marketing") return role === "admin" || role === "marketing";
  return false;
}

/** เมนูแม่ถือว่า active เมื่อ path ปัจจุบันอยู่ใต้ลูกตัวใดตัวหนึ่ง */
function childActive(item: NavItem, pathname: string): boolean {
  return (item.children ?? []).some(
    (c) => pathname === c.path || pathname.startsWith(`${c.path}/`)
  );
}

/** แถบฟ้าซ้ายของเมนูที่ active (แบบ kkautoweb) */
function ActiveBar() {
  return <span aria-hidden className="absolute bottom-2 left-0 top-2 w-1 rounded-full bg-brand-600" />;
}

function pathMatches(base: string, pathname: string): boolean {
  return base === "/" ? pathname === "/" : pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * ชื่อหน้าบน topbar จากเมนู — เลือก path ที่ยาวที่สุดที่ตรง (เช่น /xian/vs → "เชียน")
 * หน้าลูกของเมนูแม่ = "แม่ › ลูก" ให้รู้ว่าอยู่ส่วนไหน · หน้าไหนมี <PageHeader title> จะใช้ชื่อนั้นแทน
 */
function navTitle(pathname: string): string | null {
  let best: { len: number; title: string } | null = null;
  for (const section of NAV_SECTIONS) {
    for (const item of section.items) {
      const targets = item.children?.length
        ? item.children.map((c) => ({ path: c.path, title: `${item.label} › ${c.label}` }))
        : [{ path: item.path, title: item.label }];
      for (const t of targets) {
        if (pathMatches(t.path, pathname) && (!best || t.path.length > best.len)) {
          best = { len: t.path.length, title: t.title };
        }
      }
    }
  }
  return best?.title ?? null;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  marketing: "Marketing",
  user: "User",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

export function AppShell() {
  const { role, user, logout } = useAuth();
  const dataAccess = dataAccessOf(user);
  // ซ่อนเมนู บัญชีโฆษณา 2 ให้คนนอกทีม (403) เหมือนไม่มีเมนูนี้ · เช็คไม่สำเร็จ (5xx/เน็ตหลุด) = ยังโชว์เมนู
  // ให้เข้าไปเห็นแถบแจ้ง + ปุ่มลองใหม่ในหน้า แทนการหายไปเงียบ ๆ (backend ยังกันสิทธิ์ทุก endpoint อยู่)
  const kkAccess = useAccountingAccess();
  const showAdLabNav = kkAccess.allowed === true || kkAccess.error !== null;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Topbar: ชื่อที่หน้าส่งมาเอง (PageHeader) ชนะชื่อจากเมนู · actionsSlot = จุด portal ของปุ่มหลักของหน้า
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const topbarCtx = useMemo(() => ({ actionsSlot, setTitle: setPageTitle }), [actionsSlot]);
  const topbarTitle = pageTitle ?? navTitle(location.pathname) ?? "KK Marketing Hub";
  const userName = user?.display_name || user?.username || "";
  const userInitial = userName.charAt(0).toUpperCase();
  // submenu ที่ user กดกาง/หุบเอง — ค่าที่ไม่ได้แตะจะ fallback เป็น active-trail อัตโนมัติ
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  // sidebar collapse (desktop only) — ค้างใน localStorage เพราะเป็น preference ของ user
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("nav:collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("nav:collapsed", navCollapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [navCollapsed]);

  // หน้าที่ต้องการพื้นที่กว้าง — เข้ามาแล้ว auto-collapse sidebar ให้ทันที
  // user กดปุ่ม ‹ บน sidebar เพื่อกางคืนได้ ใน session เดียวกันก็ไม่ collapse อีกจนกว่าจะไปหน้าอื่นแล้วกลับ
  useEffect(() => {
    // หมายเหตุ: /ad-accounts2 ไม่อยู่ในนี้แล้ว — submenu ของ AP อยู่ใน sidebar kk จึงต้องเปิด sidebar ไว้
    const WIDE_ROUTES = ["/ad-accounts"];
    // Guard: ตั้ง state เฉพาะตอนเข้าหน้าที่ต้องการ — เลี่ยง re-render ส่วนเกินบนหน้าอื่น
    if (WIDE_ROUTES.includes(location.pathname)) setNavCollapsed(true);
    // ไม่ตั้ง false ตอนออก — เคารพ user preference จาก localStorage
  }, [location.pathname]);

  useEffect(() => {
    setMobileNavOpen(false);
    // เคลียร์ override ตอนเปลี่ยนหน้า — ให้ active trail เป็นตัวกำหนดว่า submenu ไหนกาง
    setOpenMenus({});
  }, [location.pathname]);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) prefetchDashboardRoutes();
    };
    let idleId: number;
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      idleId = window.setTimeout(run, 400) as unknown as number;
    }
    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, []);

  return (
    <div className="flex min-h-screen">
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="ปิดเมนู"
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-[min(18rem,88vw)] flex-col border-r border-slate-200/70 bg-white transition-[width,transform] duration-200 ease-out lg:static lg:z-0 lg:translate-x-0 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } ${navCollapsed ? "lg:w-16" : "lg:w-64"}`}
      >
        {/* Brand block — โครงเดียวกับ kkautoweb ต่างกันแค่ชื่อระบบ
            sidebar lg:w-64 = 288px (root 18px) − ขอบ 1 − px-3 27 − โลโก้ 40.5 − ปุ่มย่อ 40.5 − gap-2 18 = เหลือ ~161px ·
            "KK Marketing Hub" Bai Jamjuree 700 ที่ 18px กว้าง ~156px (วัดจาก hmtx ของไฟล์ฟอนต์) → ไม่ถูกตัด "…" */}
        <div
          className={`flex h-16 shrink-0 items-center gap-2 border-b border-slate-100 px-3 ${
            navCollapsed ? "lg:justify-center lg:px-0" : ""
          }`}
        >
          <div
            aria-hidden
            className={`h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-brand-800 shadow-sm ${
              navCollapsed ? "flex lg:hidden" : "flex"
            }`}
          >
            <span className="text-sm font-bold text-white">&#9670;</span>
          </div>
          <div className={`min-w-0 flex-1 leading-tight ${navCollapsed ? "lg:hidden" : ""}`}>
            <span className="block truncate font-heading font-bold text-slate-900">KK Marketing Hub</span>
            <span className="block text-xs text-slate-500">Marketing</span>
          </div>
          {/* Collapse toggle (desktop only) */}
          <button
            type="button"
            onClick={() => setNavCollapsed((v) => !v)}
            className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 lg:flex"
            title={navCollapsed ? "กางเมนู" : "ย่อเมนู (ใช้ไอคอนเท่านั้น)"}
            aria-label={navCollapsed ? "กางเมนู" : "ย่อเมนู"}
            aria-expanded={!navCollapsed}
          >
            {navCollapsed ? (
              <ChevronsRight aria-hidden className="h-5 w-5" />
            ) : (
              <ChevronsLeft aria-hidden className="h-5 w-5" />
            )}
          </button>
          <div className="shrink-0 lg:hidden">
            <CloseButton onClick={() => setMobileNavOpen(false)} />
          </div>
        </div>
        <nav aria-label="เมนูหลัก" className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
          {NAV_SECTIONS.map((section, si) => {
            const items = section.items.filter(
              (p) =>
                navVisible(role, p.role, dataAccess, kkAccess.teamLead) &&
                // บัญชีโฆษณา 2 (ระบบใหม่ native) = เห็นเฉพาะคนที่ /api/accounting/me อนุญาต (ทุก role รวม user · คนอื่นเหมือนไม่มีเมนูนี้)
                (p.path !== "/ad-accounts2" || showAdLabNav)
            );
            if (items.length === 0) return null;
            return (
              <div key={section.title ?? `top-${si}`} className="flex flex-col gap-1">
                {section.title ? (
                  <>
                    {/* โหมดย่อ (desktop): หัวหมวดกลายเป็นเส้นคั่นบางๆ ให้ยังเห็นการแบ่งกลุ่ม */}
                    {navCollapsed && (
                      <div className="mx-2 mb-1 mt-3 hidden border-t border-slate-200 lg:block" aria-hidden />
                    )}
                    <div
                      className={`px-3.5 pb-1 pt-4 text-xs font-semibold text-slate-500 ${
                        navCollapsed ? "lg:hidden" : ""
                      }`}
                    >
                      {section.title}
                    </div>
                  </>
                ) : null}
                {items.map((item) => {
                  const Icon = ICON_MAP[item.icon];
                  const kids = (item.children ?? []).filter((c) => navVisible(role, c.role, dataAccess, kkAccess.teamLead));

                  if (kids.length === 0 || navCollapsed) {
                    // เมนูเดี่ยว — หรือเมนูแม่ตอน sidebar ย่อ (ลิงก์ไปลูกตัวแรก ลูกซ่อน)
                    const parentIsActive = kids.length > 0 && childActive(item, location.pathname);
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === "/"}
                        onClick={() => setMobileNavOpen(false)}
                        title={navCollapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          `relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
                            isActive || parentIsActive
                              ? "bg-brand-50 font-semibold text-slate-900"
                              : "font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                          } ${navCollapsed ? "lg:justify-center lg:px-0" : ""}`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {(isActive || parentIsActive) && <ActiveBar />}
                            <Icon
                              aria-hidden
                              className={`h-5 w-5 shrink-0 ${item.tint} ${isActive || parentIsActive ? "" : "opacity-70"}`}
                            />
                            <span className={navCollapsed ? "lg:sr-only" : ""}>{item.label}</span>
                          </>
                        )}
                      </NavLink>
                    );
                  }

                  // เมนูแม่กางได้ — เปิดเองเมื่ออยู่ในหน้าลูก (active trail)
                  const isOpen = openMenus[item.path] ?? childActive(item, location.pathname);
                  const parentActive = childActive(item, location.pathname);
                  return (
                    <div key={item.path}>
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setOpenMenus((prev) => ({ ...prev, [item.path]: !isOpen }))
                        }
                        className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
                          parentActive
                            ? "font-semibold text-slate-900"
                            : "font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        <Icon
                          aria-hidden
                          className={`h-5 w-5 shrink-0 ${item.tint} ${parentActive ? "" : "opacity-70"}`}
                        />
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronDown
                          aria-hidden
                          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                            isOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="mb-1 mt-1 flex flex-col gap-1">
                          {kids.map((c) => (
                            <NavLink
                              key={c.path}
                              to={c.path}
                              end={c.path === item.path}
                              onClick={() => setMobileNavOpen(false)}
                              className={({ isActive }) =>
                                `relative rounded-xl py-2 pl-12 pr-3 text-sm transition-colors ${
                                  isActive
                                    ? "bg-brand-50 font-semibold text-slate-900"
                                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  {isActive && <ActiveBar />}
                                  {c.label}
                                </>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>
      </aside>
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        {/* Topbar — ชื่อหน้า + ปุ่มหลักของหน้า (TopbarActions) + ชิปผู้ใช้ · แบบเดียวกับ kkautoweb
            พื้นทึบ ไม่ใช้ backdrop-blur (blur ทำให้ scroll กระตุกบนเครื่องทีม) */}
        <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-3 border-b border-slate-200/70 bg-white px-4 sm:px-6">
          <button
            type="button"
            aria-label={mobileNavOpen ? "ปิดเมนู" : "เปิดเมนู"}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((o) => !o)}
            className="-ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-700 hover:bg-slate-100 lg:hidden"
          >
            <Menu aria-hidden className="h-6 w-6" />
          </button>
          {/* ชื่อหน้ากินที่ว่างที่เหลือ + ขั้นต่ำ 6rem ให้อ่านออก · ปุ่มในช่อง actions ใต้ sm เหลือไอคอน ([data-label] ซ่อน) */}
          <h1 className="min-w-[6rem] flex-1 truncate font-heading text-lg font-semibold text-slate-900">
            {topbarTitle}
          </h1>
          <div
            ref={setActionsSlot}
            className="flex shrink-0 items-center gap-2 empty:hidden [&_[data-label]]:hidden sm:[&_[data-label]]:inline"
          />
          {user ? (
            <div className="flex shrink-0 items-center gap-1.5 border-l border-slate-200 pl-3">
              {/* ชื่อ + รูปวงกลม → หน้าบัญชีของฉัน (/me) */}
              <NavLink
                to="/me"
                onClick={() => setMobileNavOpen(false)}
                title={`${user.username} — บัญชีของฉัน`}
                aria-label={`${userName} — บัญชีของฉัน`}
                className="flex items-center gap-2 rounded-xl p-1 pr-2 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
              >
                <div
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100"
                >
                  <span className="text-sm font-semibold text-brand-700">{userInitial}</span>
                </div>
                <div aria-hidden className="hidden leading-tight md:block">
                  <p className="max-w-[10rem] truncate text-sm font-medium text-slate-900">
                    {userName}
                  </p>
                  <p className="text-xs text-slate-500">{roleLabel(user.role)}</p>
                </div>
              </NavLink>
              <button
                type="button"
                onClick={() => {
                  logout();
                  setMobileNavOpen(false);
                  navigate("/login", { replace: true });
                }}
                className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600"
                title="ออกจากระบบ"
                aria-label="ออกจากระบบ"
              >
                <LogOut aria-hidden className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <NavLink
              to="/login"
              className="rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-muted"
            >
              เข้าสู่ระบบ
            </NavLink>
          )}
        </header>
        {/* หน้าเลื่อนด้วย window (ไม่ใส่ overflow ที่ main) — ถ้า main เป็น overflow-auto โดยไม่มีความสูงจำกัด
            มันจะกลายเป็น scroll container ที่ไม่เคยเลื่อน แล้ว sticky ในหน้าจะไม่ลอยเลย · ตารางกว้างเลื่อนในกรอบ overflow-x-auto ของตัวเอง */}
        <main
          className={`flex-1 bg-surface ${
            location.pathname.startsWith("/ad-accounts2") || location.pathname.startsWith("/ad-lab") ? "p-0" : "p-4 sm:p-6 lg:p-8"
          }`}
        >
          <TopbarContext.Provider value={topbarCtx}>
            <DateRangeProvider>
              <RouteErrorBoundary>
                <Suspense fallback={<Spinner className="min-h-[50vh]" />}>
                  <Outlet />
                </Suspense>
              </RouteErrorBoundary>
            </DateRangeProvider>
          </TopbarContext.Provider>
        </main>
      </div>
      {/* Global toast renderer — pushToast() จากหน้าไหนก็ตามจะแสดงผ่าน container ตัวนี้
          ก่อนหน้ามี ToastContainer แค่ใน AdAccountsPage → ปุ่มอื่นๆ ที่เรียก pushToast
          จากหน้าอื่นแสดง toast ไม่ได้ ทำให้ดูเหมือนไม่มีอะไรเกิดขึ้น */}
      <ToastContainer />
    </div>
  );
}
